/**
 * Every sentence the client may show for Google sign-in. A status or code only
 * selects one of these; server, library and URL text is never displayed.
 */
export const externalSignInMessages = {
  unavailable: 'Google sign-in is not available.',
  rateLimited: 'Too many attempts. Wait a minute, then try again.',
  startFailed: 'Google sign-in could not be started. Try again.',
  signInAgain: 'Sign in again before linking Google.',
  cancelled: 'Google sign-in was cancelled.',
  verifyFirst: 'An account with this email address exists but is not verified yet. Verify the address with the link that was mailed to you, then sign in again.',
  unverified: 'Verify your email address before signing in with Google.',
  differentEmail: 'That Google account has a different email address than your account here. Choose the Google account with the same email address.',
  linkedElsewhere: 'That Google account is already linked to another account here. Choose a different Google account.',
  linkFailed: 'That Google account could not be linked to your account here. Try again.',
  signUpClosed: 'No account matches this Google account. Create an account with your email first.',
  invalidName: 'The name on your Google account cannot be used here. Create an account with your email instead.',
  accountFailed: 'Your account could not be created. Try again.',
  sessionFailed: 'You could not be signed in. Try again.',
  expired: 'That Google sign-in attempt expired or was interrupted. Start again.',
  provider: 'Google could not complete the sign-in. Try again.',
  serverFailed: 'Something went wrong on our side. Try again in a moment.',
  returnFailed: 'That sign-in or link could not be completed. Try again.',
} as const;

export type ExternalSignInMessage = keyof typeof externalSignInMessages;
export type ExternalSignInStartFailure = 'unavailable' | 'rateLimited' | 'startFailed' | 'signInAgain';

/** The only rejection of AuthGateway.signInWithGoogle and linkGoogle; its message is always fixed text. */
export class ExternalSignInError extends Error {
  constructor(readonly reason: ExternalSignInStartFailure) {
    super(externalSignInMessages[reason]);
    this.name = 'ExternalSignInError';
  }
}

/**
 * `?error=<code>` values a failed provider return can carry, under the sentence
 * each one selects: every code the server lists for a failed return
 * (`specs/features/backend/google-sign-in.md`, "Codes the client will see"),
 * which are codes Better Auth 1.7.7 emits from its OAuth callback, the
 * provider's own OAuth error codes and this server's own codes (INVALID_NAME,
 * LINK_SESSION_CHANGED); and the callback codes of the library that this
 * server's configuration does not emit today.
 *
 * To add a code, write it in the list of its sentence, in any letter case. A
 * code in no list gets `returnFailed`.
 *
 * `account_not_linked`: the server links a verified local account with the
 * same email by itself, so this refusal is left for a local account that is
 * not verified yet, and the sentence says so. (The library answers with the
 * same code when a local account exists and Google does not report the email
 * of the Google account verified.)
 */
const returnCodes: readonly (readonly [ExternalSignInMessage, readonly string[]])[] = [
  ['cancelled', ['access_denied']],
  ['verifyFirst', ['account_not_linked']],
  ['unverified', ['email_not_verified']],
  ['signInAgain', ['LINK_SESSION_CHANGED', 'SESSION_NOT_FRESH']],
  ['differentEmail', ['email_does_not_match']],
  ['linkedElsewhere', ['account_already_linked_to_different_user']],
  ['linkFailed', ['unable_to_link_account']],
  ['signUpClosed', ['signup_disabled']],
  ['invalidName', ['INVALID_NAME']],
  ['accountFailed', ['unable_to_create_user']],
  ['sessionFailed', ['unable_to_create_session']],
  ['unavailable', ['oauth_provider_not_found']],
  ['expired', ['state_mismatch', 'state_not_found', 'state_invalid', 'invalid_callback_request', 'no_code', 'invalid_code']],
  ['provider', ['unable_to_get_user_info', 'email_not_found', 'issuer_mismatch', 'issuer_missing', 'nonce_binding_missing', 'server_error', 'temporarily_unavailable']],
  ['serverFailed', ['internal_server_error', 'validation_failed']],
];

/** Keyed in lower case: a code may arrive in either letter case. A Map, not an object, so that `constructor` or `__proto__` cannot select anything. */
const returnMessages = new Map<string, string>(returnCodes.flatMap(([message, codes]) =>
  codes.map((code): [string, string] => [code.toLowerCase(), externalSignInMessages[message]])));

/** The code is used for this lookup only; it is never stored, logged or shown. */
export function externalSignInReturnMessage(code: string): string {
  return returnMessages.get(code.toLowerCase()) ?? externalSignInMessages.returnFailed;
}
